import { useState, useEffect } from 'react';
import { Row, Col, Space, message } from 'antd';
import { ColumnsType } from 'antd/es/table'
import { ReModal, ReButton } from "@/components/refineElement";
import ResponsiveTable from '@/components/responsiveTable';
import { sortColumn } from "@/lib/antdElementFunction";
import { getLangList } from '@/lib/i18n';
import { hasPermission } from "@/lib/permissionUtil";
import { getSubjectColumnsByOrder, getFormatData, textScoreList, isTextScore } from "@/lib/transcript";
import TranscriptConfig from "@/config/transcript";
import { ClassSubjectListType, ClassType } from "@/containers/organization/classManagement/types";
import { StudentType } from "@/containers/humanResourceManagement/studentManagement/types";
import ModalIndividualScoring from './ModalIndividualScoring';
import ModalTranscriptType1 from './ModalTranscriptType1';
import ModalTranscriptType2 from './ModalTranscriptType2';
import { TranscriptListType, TranscriptScoreType } from './types';

const { updateTranscript } = TranscriptConfig;

type Props = {
  studentArray: StudentType[];
  transcriptList: TranscriptListType;
  examId: number;
  getTranscriptListFunction: () => void;
  subjectInfo: ClassSubjectListType;
  classInfo: ClassType;
  year: string;
}

function StudentTable(props: Props) {
  const {
    studentArray,
    transcriptList,
    examId,
    getTranscriptListFunction,
    subjectInfo,
    classInfo,
    year,
  } = props;
  const [studentInfo, setStudentInfo] = useState({} as StudentType);
  const [transcriptScoreArray, setTranscriptScoreArray] = useState<TranscriptScoreType['students']>();
  const [columns, setColumns] = useState<ColumnsType<StudentType>>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [handleType, setHandleType] = useState('');
  const { template } = classInfo;
  const subjectListForExam = subjectInfo[examId];
  const formatedData = getFormatData(studentArray, transcriptList);
  const studentListT: any = getLangList("studentList");
  const transcriptT: any = getLangList("transcript");
  const errorT: any = getLangList("error");
  const buttonT: any = getLangList("button");

  useEffect(() => {
    const dynamicColumns = getSubjectColumnsByOrder(template, subjectListForExam);

    const averageScoreArray = dynamicColumns.map((item) => {
      const { dataIndex } = item;
      let scoreBase = 0;
      const averageScore = formatedData.reduce((acc: number, cur: StudentType) => {
        const score = cur[dataIndex];
        const formatedScore = isTextScore(score) ? textScoreList[score as keyof typeof textScoreList]?.average : score;

        formatedScore && scoreBase++;
        return acc + (formatedScore ? parseFloat(`${formatedScore}`) : 0);
      }, 0) / scoreBase;

      return (Math.round(averageScore * 10) / 10) || 0;
    });

    const formatedDynamicColumns = dynamicColumns.map((item, index) => ({
      ...item,
      title: (
        <Row>
          <Col span={24}>{`(${averageScoreArray[index] || '-'})`}</Col>
          <Col span={24}>{item.title}</Col>
        </Row>
      ),
    }));

    const transcriptScoreArray = dynamicColumns.map((item) => ({
      subjectId: item.dataIndex,
      subjectEnName: item.title,
      subjectScore: '',
      studentId: 0,
    }));

    const fixedColumns: ColumnsType<StudentType> = [
      {
        title: studentListT.SEAT_NO,
        dataIndex: "cramSeat",
        sorter: (a: any, b: any) => sortColumn(a.cramSeat, b.cramSeat),
        sortDirections: ['ascend', 'descend', 'ascend'],
        defaultSortOrder: 'ascend',
      },
      { title: studentListT.ACCOUNT, dataIndex: "username" },
      { title: studentListT.CN_NAME, dataIndex: "nameCn" },
      { title: studentListT.EN_NAME, dataIndex: "nameEn" },
      { title: studentListT.AVERAGE_SCORE, dataIndex: "averageScore" },
    ];
    const actionColumns = [
      {
        title: studentListT.OPERATE,
        dataIndex: "action",
        render: (text: any, record: any) => (
          <Space>
            {hasPermission('TRANSCRIPT_MANAGEMENT_EDIT') &&
              <ReButton iconName='edit' onClick={clickIndividualScoring(record)}>
                {transcriptT.SCORING}
              </ReButton>
            }

            <ReButton iconName='book' onClick={clickTranscript(record)}>
              {transcriptT.TRANSCRIPT}
            </ReButton>
          </Space>
        ),
      }
    ];

    setTranscriptScoreArray(transcriptScoreArray);
    setColumns([...fixedColumns, ...formatedDynamicColumns, ...actionColumns]);
  }, [template, subjectListForExam, transcriptList]);

  const clickIndividualScoring = (data: StudentType) => (e: any) => {
    e.stopPropagation();
    setStudentInfo(data);
    toggleModal('updateTranscript');
  };

  const clickTranscript = (data: StudentType) => (e: any) => {
    e.stopPropagation();
    setStudentInfo(data);
    toggleModal('viewTranscript');
  };

  function updateTranscriptFunction(values: { [subjectId: string]: string }) {
    const { userAccountId } = studentInfo;
    const subjectIdArray = Object.keys(values);
    const students = [];
    const otherStudentArray = formatedData.filter((item) => item.userAccountId !== userAccountId);
    const editingStudent = Object.keys(values).map((item) => ({ studentId: userAccountId, subjectId: item, subjectScore: values[item]?.toUpperCase() }));

    otherStudentArray.forEach((student: StudentType) => {
      const studentScore = subjectIdArray.map((subjectId) => ({ studentId: student.userAccountId, subjectId, subjectScore: student[parseInt(subjectId)]?.toUpperCase() || '' }));

      students.push(...studentScore);
    });

    students.push(...editingStudent);

    if (!hasPermission('TRANSCRIPT_MANAGEMENT_EDIT')) {
      return message.error(errorT.NO_PERMISSION);
    }

    updateTranscript({ examId, students }).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      toggleModal();
      getTranscriptListFunction();
    });
  };

  function toggleModal(handleType: string = '') {
    setIsModalVisible(!isModalVisible);
    setHandleType(handleType);
  };

  return columns.length > 0 ? (
    <>
      <Row gutter={[0, 12]}>
        <Col span={24}>
          <ResponsiveTable
            dataSource={formatedData}
            columns={columns}
            className="transcript"
          />
        </Col>
      </Row>

      {isModalVisible && handleType === 'updateTranscript' &&
        <ReModal
          title={transcriptT.SCORING}
          onCancel={toggleModal}
          visible={isModalVisible}
          onOk={updateTranscriptFunction}
          CollectedForm={ModalIndividualScoring}
          formProps={{
            info: studentInfo,
            transcriptScoreArray,
          }}
          width={'700px'}
        />
      }

      {isModalVisible && handleType === 'viewTranscript' &&
        <ReModal
          title={transcriptT.TRANSCRIPT}
          onCancel={toggleModal}
          visible={isModalVisible}
          onOk={() => { }}
          CollectedForm={template === 1 ? ModalTranscriptType1 : ModalTranscriptType2}
          formProps={{
            info: studentInfo,
            subjectListForExam,
            classInfo,
            year,
          }}
          width={'851px'}
          okText={buttonT.PRINT}
          Footer={null}
        />
      }
    </>
  ) : null;
}

export default StudentTable;